import type { ReactElement, ReactNode, Ref } from 'react'

import { PillButton } from '../pill/pill.tsx'
import { SkipLink } from '../skip-link/skip-link.tsx'

export interface NavigationItem {
  readonly href: string
  readonly label: string
  /**
   * Id of the section this item points at, when it points at one on this page.
   * Matched against `activeSectionId` for scroll-spy; absent on a route link.
   */
  readonly sectionId?: string
  /**
   * Set by a ROUTE item that points at the document being rendered.
   *
   * Separate from the scroll-spy match above because the two are different facts
   * and take different values: a section the reader has scrolled to is
   * `aria-current="true"`, while a link to the page they are already on is
   * `aria-current="page"`. Collapsing them would make one of the two lie.
   */
  readonly current?: boolean
}

export interface PageShellProps {
  readonly siteName: string
  /** Small-caps qualifier beside the brand: `poemas`. */
  readonly brandSuffix?: string
  readonly navigationLabel: string
  readonly navigation: readonly NavigationItem[]
  /** Supplied by the scroll-spy hook on the home route; `null` everywhere else. */
  readonly activeSectionId?: string | null
  /**
   * Whether the navigation is disclosed. Only observable below the breakpoint at
   * which the nav becomes permanent chrome — above it the stylesheet shows the
   * list unconditionally and hides the toggle, so this flag stops mattering.
   */
  readonly navigationExpanded: boolean
  readonly onNavigationToggle: () => void
  /**
   * Static, and deliberately not "Abrir menu" / "Fechar menu".
   *
   * `aria-expanded` already carries the state, and a label that restates it makes
   * a screen reader announce the same fact twice in two vocabularies. The APG
   * disclosure pattern names the thing, not the direction.
   */
  readonly navigationToggleLabel: string
  /** See `PillButton.buttonRef` — this is how focus comes back on collapse. */
  readonly navigationToggleRef?: Ref<HTMLButtonElement>
  /**
   * Fired when a navigation item is activated, so the owner can collapse the
   * menu. An open panel left standing after an anchor jump covers the very
   * heading it just scrolled to.
   */
  readonly onNavigate?: () => void
  /** Injected by the app so this component stays hook-free. */
  readonly themeToggle?: ReactNode
  /** Second sticky bar, below the header. Used by the poem route. */
  readonly subNav?: ReactNode
  readonly skipLinkLabel: string
  readonly footerDomain: string
  readonly footerNote: string
  /**
   * Third-party credit line, injected as a node because attribution generally
   * has to carry a link — to the work, to its licence, or to both — and a plain
   * string cannot. Optional: a shell with nothing to credit renders no line at
   * all rather than an empty one.
   */
  readonly footerCredit?: ReactNode
  readonly children: ReactNode
  /** Landmark id targeted by the skip link. Overridable for embedded shells. */
  readonly mainId?: string
}

/**
 * `page` for the route you are on, `true` for the section you have scrolled to,
 * nothing otherwise — and `undefined` is what keeps the attribute off the element
 * entirely rather than writing `aria-current="false"`, which announces.
 */
function ariaCurrentFor(
  item: NavigationItem,
  activeSectionId: string | null,
): 'page' | 'true' | undefined {
  if (item.current === true) {
    return 'page'
  }

  return item.sectionId !== undefined && item.sectionId === activeSectionId ? 'true' : undefined
}

export const DEFAULT_MAIN_ID = 'conteudo-principal'

/** Target of the toggle's `aria-controls`. One nav per document, so one id. */
export const NAVIGATION_ID = 'navegacao-principal'

/**
 * Semantic document scaffold — `<header>` / `<nav>` / `<main>` / `<footer>`.
 *
 * Pure presenter (rules/frontend.md § 1): no state, no effects, no data access.
 * It gained slots in this phase, not logic — `themeToggle` and `subNav` arrive as
 * nodes so the container in `app.tsx` keeps every hook.
 *
 * THE NAVIGATION DISCLOSURE IS CONTROLLED, for the same reason. A `useState` here
 * would make this the one component in the design system that owns something, and
 * the Escape key and the focus return both need to be coordinated with it — so
 * `use-navigation-menu.ts` owns all three and this renders what it is told.
 *
 * Exactly one of each landmark per document. The single `<h1>` is owned by the
 * page rendered into `children`, keeping the heading hierarchy contiguous from h1
 * downwards (rules/frontend.md § 5).
 */
export function PageShell({
  siteName,
  brandSuffix,
  navigationLabel,
  navigation,
  activeSectionId = null,
  navigationExpanded,
  onNavigationToggle,
  navigationToggleLabel,
  navigationToggleRef,
  onNavigate,
  themeToggle,
  subNav,
  skipLinkLabel,
  footerDomain,
  footerNote,
  footerCredit,
  children,
  mainId = DEFAULT_MAIN_ID,
}: PageShellProps): ReactElement {
  return (
    <div className="page-shell">
      <SkipLink targetId={mainId} label={skipLinkLabel} />

      <header className="page-shell__header">
        <div className="page-shell__header-inner">
          {/* An `<a href="/">`, not the design's `#inicio`: that anchor resolves to
              nothing on a poem document, whereas a real path works from anywhere. */}
          <a className="page-shell__brand" href="/">
            <span className="page-shell__brand-name">{siteName}</span>
            {/* The literal space is load-bearing: a flex `gap` separates the two
                spans visually, but the accessible name is built from text content,
                where adjacent spans concatenate into "Maria Cavalheiropoemas". */}
            {brandSuffix === undefined ? null : (
              <>
                {' '}
                <span className="page-shell__brand-suffix">{brandSuffix}</span>
              </>
            )}
          </a>

          {/* BEFORE the nav in document order, which is the whole point: a reader
              who opens the menu tabs straight into it rather than having to
              shift-tab backwards past it. */}
          {/* ICON-ONLY, WITH THE LABEL IN HIDDEN TEXT — the same shape as the theme
              toggle sitting beside it, and for a reason beyond consistency. With
              "Menu" written out the control is 114px wide, which leaves the brand
              153px in a 416px header and breaks "Maria Cavalheiro" across two
              lines. At 44px the one-row header fits from 375px up, which is every
              phone this site is likely to meet. § 2.5.3 Label in Name has nothing
              to object to: there is no visible text label to disagree with. */}
          <PillButton
            variant="secondary"
            className="page-shell__nav-toggle"
            onClick={onNavigationToggle}
            expanded={navigationExpanded}
            controls={NAVIGATION_ID}
            buttonRef={navigationToggleRef}
            label={navigationToggleLabel}
            icon={navigationExpanded ? 'close' : 'list'}
          >
            <span className="visually-hidden">{navigationToggleLabel}</span>
          </PillButton>

          {/* NOT the `hidden` attribute, and not conditional rendering.
              `[hidden]` carries `display: none !important`, which would follow the
              nav up past the breakpoint where it is meant to be permanent chrome;
              and unmounting the links would take four of them out of the
              prerendered HTML, where they are part of the crawlable link graph.
              A data attribute lets one collapsed state exist on phones and no
              collapsed state at all on a laptop. */}
          <nav
            className="page-shell__nav"
            id={NAVIGATION_ID}
            data-expanded={navigationExpanded ? 'true' : 'false'}
            aria-label={navigationLabel}
          >
            <ul className="page-shell__nav-list">
              {navigation.map((item) => {
                const current = ariaCurrentFor(item, activeSectionId)

                return (
                  <li key={item.href} className="page-shell__nav-item">
                    <a
                      className={`page-shell__nav-link${current === undefined ? '' : ' page-shell__nav-link--is-active'}`}
                      href={item.href}
                      aria-current={current}
                      onClick={onNavigate}
                    >
                      {item.label}
                    </a>
                  </li>
                )
              })}
            </ul>
          </nav>

          <div className="page-shell__header-actions">{themeToggle}</div>
        </div>
      </header>

      {subNav}

      <main className="page-shell__main" id={mainId} tabIndex={-1}>
        {children}
      </main>

      <footer className="page-shell__footer">
        <div className="page-shell__footer-inner">
          <span className="page-shell__footer-domain">{footerDomain}</span>
          <span className="page-shell__footer-note">{footerNote}</span>
          {footerCredit === undefined ? null : (
            <span className="page-shell__footer-credit">{footerCredit}</span>
          )}
        </div>
      </footer>
    </div>
  )
}
